import { useEffect, useState } from "react";
import type { IconSet } from "../../config/icon-sets";
import {
  loadIconSetManifest,
  loadIconTemplates,
  renderIconSvg,
  svgDataUrl,
  defaultIconConfig,
  type IconColorConfig,
  type IconSetManifest,
} from "../../libs/icon-sprite-builder";
import { loadSpriteIndex, resolveIconSetUrl, type SpriteIndex } from "../../libs/sprite";
import { SpriteIcon } from "./sprite-icon";

/** An icon set ready to draw: a ready-made sprite, or SVG templates. */
export type LoadedIconSet =
  | { kind: "sprite"; index: SpriteIndex }
  | { kind: "svg"; manifest: IconSetManifest; templates: Record<string, string> };

export type IconSetLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; loaded: LoadedIconSet };

export async function loadIconSet(set: IconSet, apiKey?: string): Promise<LoadedIconSet> {
  if (set.kind === "sprite") return { kind: "sprite", index: await loadSpriteIndex(resolveIconSetUrl(set), apiKey) };
  const manifest = await loadIconSetManifest(set.path);
  return { kind: "svg", manifest, templates: await loadIconTemplates(set.path, manifest) };
}

/** Load a set for display; also accepts a raw sprite URL (custom style sprite). */
export function useIconSet(source: IconSet | string | null, apiKey?: string): IconSetLoadState {
  const [state, setState] = useState<IconSetLoadState>({ status: "loading" });
  useEffect(() => {
    if (!source) {
      setState({ status: "error", message: "Style không có sprite" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    const p =
      typeof source === "string"
        ? loadSpriteIndex(source, apiKey).then((index): LoadedIconSet => ({ kind: "sprite", index }))
        : loadIconSet(source, apiKey);
    p.then((loaded) => !cancelled && setState({ status: "ready", loaded })).catch(
      (e) => !cancelled && setState({ status: "error", message: e instanceof Error ? e.message : String(e) })
    );
    return () => {
      cancelled = true;
    };
  }, [source, apiKey]);
  return state;
}

export function iconNames(loaded: LoadedIconSet): string[] {
  return Object.keys(loaded.kind === "sprite" ? loaded.index.atlas : loaded.manifest.icons);
}

/** One icon of a loaded set, fitted in a `size`px square. SVG sets use `config`. */
export function SetIcon({
  loaded,
  name,
  size,
  config,
}: {
  loaded: LoadedIconSet;
  name: string;
  size: number;
  config?: IconColorConfig;
}) {
  if (loaded.kind === "sprite") return <SpriteIcon index={loaded.index} name={name} size={size} />;
  const template = loaded.templates[name];
  if (!template) return <span style={{ width: size, height: size }} />;
  const svg = renderIconSvg(loaded.manifest, name, template, config ?? defaultIconConfig(loaded.manifest));
  return (
    <img
      src={svgDataUrl(svg)}
      alt=""
      title={name}
      draggable={false}
      className="inline-block shrink-0 object-contain"
      style={{ width: size, height: size }}
    />
  );
}
